import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  cloudHealth,
  cloudListUsers,
  ensureCloudSession,
  cloudCreateUser,
  cloudRemoveUser,
  cloudUpdateUser,
  getCloudToken,
  mapApiUser,
} from "../api/client";
import { useAuth } from "../auth/AuthContext";
import { users as seed, type StaffUser } from "./mock";

export type { StaffUser };

interface UsersState {
  list: StaffUser[];
  cloudReady: boolean;
  syncing: boolean;
  add: (user: StaffUser & { password: string }) => Promise<void>;
  update: (user: StaffUser) => Promise<void>;
  remove: (id: string) => Promise<void>;
  getById: (id: string) => StaffUser | undefined;
  refreshFromCloud: () => Promise<void>;
}

const UsersContext = createContext<UsersState | null>(null);

function initialUsers(): StaffUser[] {
  return seed.map((u) => ({ ...u }));
}

export function UsersProvider({ children }: { children: ReactNode }) {
  const { email, isAuthenticated } = useAuth();
  const [list, setList] = useState<StaffUser[]>(initialUsers);
  const [cloudReady, setCloudReady] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const mergeCloudItems = useCallback((items: ReturnType<typeof mapApiUser>[]) => {
    setList((prev) => {
      const localOnly = prev.filter((u) => !items.some((api) => api.id === u.id));
      return [...items, ...localOnly];
    });
  }, []);

  const refreshFromCloud = useCallback(async () => {
    setSyncing(true);
    try {
      const ok = await cloudHealth();
      if (!ok) {
        setCloudReady(false);
        return;
      }
      if (!getCloudToken() && email) {
        try {
          const sess = await ensureCloudSession(email);
          if (sess.ok === false) throw new Error(sess.message);
        } catch {
          setCloudReady(false);
          return;
        }
      }
      if (!getCloudToken()) {
        setCloudReady(false);
        return;
      }
      const { items } = await cloudListUsers();
      mergeCloudItems(items.map((u) => mapApiUser(u)));
      setCloudReady(true);
    } catch {
      setCloudReady(false);
    } finally {
      setSyncing(false);
    }
  }, [email, mergeCloudItems]);

  useEffect(() => {
    if (!isAuthenticated) return;
    void refreshFromCloud();
    const t = window.setInterval(() => void refreshFromCloud(), 15000);
    return () => window.clearInterval(t);
  }, [isAuthenticated, refreshFromCloud]);

  const add = useCallback(async (user: StaffUser & { password: string }) => {
    const result = await cloudCreateUser({
      email: user.email,
      name: user.name,
      password: user.password,
      role: user.role,
      projectIds: user.projectIds,
    });
    setList((prev) => [mapApiUser(result), ...prev]);
  }, []);

  const update = useCallback(async (user: StaffUser) => {
    await cloudUpdateUser(user.id, {
      name: user.name,
      role: user.role,
      enabled: user.enabled,
      projectIds: user.projectIds,
    });
    setList((prev) => prev.map((u) => (u.id === user.id ? user : u)));
    void refreshFromCloud();
  }, [refreshFromCloud]);

  const remove = useCallback(async (id: string) => {
    await cloudRemoveUser(id);
    setList((prev) => prev.filter((u) => u.id !== id));
  }, []);

  const value = useMemo<UsersState>(
    () => ({
      list,
      cloudReady,
      syncing,
      add,
      update,
      remove,
      getById: (id) => list.find((u) => u.id === id),
      refreshFromCloud,
    }),
    [list, cloudReady, syncing, add, update, remove, refreshFromCloud],
  );

  return <UsersContext.Provider value={value}>{children}</UsersContext.Provider>;
}

export function useUsers() {
  const ctx = useContext(UsersContext);
  if (!ctx) {
    throw new Error("useUsers must be used within UsersProvider");
  }
  return ctx;
}
